/**
 * Seed data utility - Run this from browser console after logging in
 * Usage: Import and call seedAllData() from your app
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

const HUNT_ITEMS = [
  {
    title: "SockTinder",
    desc: "AI-powered dating app for lonely socks. Computer vision matches your lost sock with its soulmate across citywide laundromat databases.",
    tags: ["AI", "Dating", "Hardware"],
    author: "drunk_elon",
    color: "from-pink-500 to-orange-400",
    icon: "🧦",
    comments: [
      "I have 47 unmatched socks. This is my salvation.",
      "Computer vision for socks? What timeline are we living in?? Take my money!",
      "Does it work with international sock standards? My Italian socks need love too.",
      "Finally a use case for AI that actually matters"
    ],
    features: [
      "Add pattern recognition for striped socks",
      "Integration with washing machine IoT sensors",
      "Sock DNA testing for perfect matches",
      "Premium tier: cashmere sock priority matching"
    ]
  },
  {
    title: "SmartCoaster 3000",
    desc: "IoT beer coaster that automatically orders next pint when weight drops below 50g. Never run dry again. Blockchain verified.",
    tags: ["IoT", "Hardware"],
    author: "beer_engineer",
    color: "from-yellow-400 to-amber-600",
    icon: "🍺",
    comments: [
      "Shut up and integrate with my bar tab!",
      "Does it work with wine glasses? Asking for a friend...",
      "The blockchain verification is what sold me. Finally, trustless beer ordering.",
      "My liver says no but my wallet says YOLO"
    ],
    features: [
      "Add drink preference learning algorithm",
      "Integration with Uber for ride home detection",
      "Happy hour price alerts",
      "Blood alcohol level sync (for responsible drinking)"
    ]
  },
  {
    title: "Ex-Blocker Pro",
    desc: "Browser extension replacing all ex photos on social media with cute capybaras. Mental health through rodent therapy.",
    tags: ["Privacy", "Wellness", "AI"],
    author: "heartbreak_dev",
    color: "from-blue-400 to-cyan-500",
    icon: "🚫",
    comments: [
      "Why capybaras specifically? (Also where do I download this)",
      "This is cheaper than therapy and more effective",
      "Can we add an option for quokkas instead?",
      "My therapist literally just recommended this. She's a genius."
    ],
    features: [
      "Custom animal selection (otters, red pandas, etc)",
      "AI detection of mutual friends to avoid awkwardness",
      "Automatic block on anniversary dates",
      "Integration with dating apps to prevent ex discovery"
    ]
  },
  {
    title: "Uber for Pigeons",
    desc: "Trained pigeons deliver micro-cargo (USB drives, SD cards) across the city. No traffic jams, works for seeds. 5G enabled.",
    tags: ["Logistics", "Green Tech"],
    author: "bird_brain",
    color: "from-green-400 to-emerald-500",
    icon: "🐦",
    comments: [
      "Finally, a green alternative to drone delivery!",
      "How do you train pigeons to use 5G? Need more details",
      "This is either the dumbest or smartest thing I've seen today",
      "RFC 1149 made real! The internet was designed for this!"
    ],
    features: [
      "Real-time pigeon tracking via GPS collars",
      "Weather delay notifications",
      "Pigeon performance ratings and reviews",
      "Express service with racing pigeons"
    ]
  },
  {
    title: "NFT Toilet Paper",
    desc: "Each roll is unique on blockchain. Burn tokens in real-time as you use them. Proof-of-Wipe consensus mechanism.",
    tags: ["Web3", "NFT", "Consumer"],
    author: "crypto_plumber",
    color: "from-purple-500 to-pink-500",
    icon: "🧻",
    comments: [
      "Proof-of-Wipe is the consensus mechanism we deserve",
      "Can I stake my unused rolls for passive income?",
      "This is peak Web3 and I'm here for it",
      "My portfolio is literally going down the toilet. Perfect."
    ],
    features: [
      "Royalty system for original minters",
      "Biodegradable NFT metadata",
      "Limited edition rolls by celebrity designers",
      "DAO governance for ply count decisions"
    ]
  },
  {
    title: "LinkedIn for Introverts",
    desc: "Professional network where messaging is completely banned. Just silently endorse skills with gentle nod emojis. Peaceful networking.",
    tags: ["Social", "Wellness"],
    author: "quiet_networker",
    color: "from-slate-500 to-gray-600",
    icon: "🤐",
    comments: [
      "As an introvert, this is literally perfect",
      "No more 'I'd like to add you to my professional network'? SOLD",
      "Can I also disable profile views? Please?",
      "Finally I can network without the 'working' part"
    ],
    features: [
      "Invisible mode by default",
      "Automatic 'seen' receipt disabling",
      "AI-generated polite decline messages",
      "Emoji-only job applications"
    ]
  },
  {
    title: "Shazam for Baby Crying",
    desc: "AI identifies why baby screams: hungry, sleepy, needs change, or existential dread of consciousness itself.",
    tags: ["AI", "Parenting"],
    author: "tired_parent",
    color: "from-red-500 to-orange-500",
    icon: "👶",
    comments: [
      "The existential dread detection is too real",
      "Does it work at 3am? That's when I need it most",
      "Can it detect 'mom vs dad' preference crying?",
      "This would have saved my marriage. Building this ASAP"
    ],
    features: [
      "Integration with smart home to adjust environment",
      "Cry pattern learning over time",
      "Twin baby simultaneous detection",
      "Stress level monitoring for parents"
    ]
  },
  {
    title: "Blockchain Umbrella",
    desc: "Smart umbrella that mints NFT for every raindrop it blocks. Trade your rain protection history on OpenSea.",
    tags: ["Web3", "Weather", "IoT"],
    author: "rain_trader",
    color: "from-cyan-500 to-blue-600",
    icon: "☂️",
    comments: [
      "Seattle residents about to become crypto millionaires",
      "What's the gas fee per raindrop? Need ROI calculations",
      "This is the most unnecessary thing I've ever needed",
      "UK market bout to go CRAZY with this"
    ],
    features: [
      "Rare raindrop NFTs during storms",
      "Integration with weather prediction APIs",
      "Umbrella-to-umbrella raindrop trading",
      "Carbon credit generation per usage"
    ]
  },
  {
    title: "Startup Idea Generator Generator",
    desc: "AI that generates AI tools that generate startup ideas. It's generators all the way down. Infinite recursion included.",
    tags: ["AI", "Meta", "SaaS"],
    author: "meta_founder",
    color: "from-indigo-500 to-purple-600",
    icon: "🎲",
    comments: [
      "We've reached peak meta and I love it",
      "Does it generate generators that generate this generator?",
      "My brain hurts but my wallet is ready",
      "This is either brilliant or we're all doomed. Probably both."
    ],
    features: [
      "Recursive depth level selector",
      "AI model fine-tuning on failed startups",
      "Integration with YC application forms",
      "Automatic pitch deck generation"
    ]
  },
  {
    title: "TikTok for Code Reviews",
    desc: "15-second code review videos with trending sounds. Swipe up to merge. Comments are just emoji reactions to your PR.",
    tags: ["Developer Tools", "Social"],
    author: "zoomer_dev",
    color: "from-pink-400 to-rose-500",
    icon: "💻",
    comments: [
      "This is how we get Gen Z into open source",
      "Can I duet my own PR reviews? For the drama?",
      "Does it have a 'For You' page with controversial merge conflicts?",
      "My PR just went viral. 10M views. Still not merged."
    ],
    features: [
      "Trending hashtags for bug types (#MemoryLeak)",
      "Viral code snippet templates",
      "Influencer code reviewers",
      "Dance challenges for successful deployments"
    ]
  }
];

const STORIES = [
  {
    author: "Alex The Drunk Dev",
    title: "How I bought a $3000 domain and forgot why",
    text: "2am. Third whiskey. Discussing Web3 with college roommate on Zoom. Brilliant idea: marketplace for air in the metaverse. Opens GoDaddy. Searches 'AirBnB-But-For-Air.com' - AVAILABLE! Buys premium listing for $2,997. Next morning: negative bank balance, confused wife, and a domain I can't even pronounce sober. Still own it. Still no use case. Anyone want to buy metaverse air rights?"
  },
  {
    author: "CryptoQueen",
    title: "I accidentally deployed to prod on Friday night after margaritas",
    text: "Never. Do you hear me. NEVER merge 'feature/fix-everything' after third cocktail at happy hour. Now our e-commerce site sells everything for $0.00, customer support is offline until Monday, and my Slack is exploding. CEO messaged '???' at 11pm. I'm currently hiding in bathroom pretending I'm in different timezone. Resume template recommendations welcome."
  },
  {
    author: "StartupSteve",
    title: "Built entire MVP in drunk coding session, can't understand it sober",
    text: "Friday 10pm: bottle of wine, lo-fi beats, pure flow state. Built amazing SaaS app in 6 hours. 200 lines of genius code. Zero comments. Variable names like 'xxx', 'thingy', 'plz_work'. Architecture makes zero sense sober. It WORKS though. Has 100 users now. Been 3 months. Too afraid to touch the code. Hired junior dev to 'maintain legacy system'. It's MY code from 3 months ago."
  },
  {
    author: "VCVictor",
    title: "Accidentally invested in startup during Zoom happy hour",
    text: "Pitch: 'Uber for houseplants'. Thought they said 'power plants'. Signed $50k term sheet. Realized mistake next day. They actually water your plants when you travel. Somehow became profitable. Now my best investment. Never reading pitch decks sober again."
  },
  {
    author: "DesignDiana",
    title: "Redesigned entire product UI in Figma while wine drunk",
    text: "Wine Wednesday turned into accidental all-nighter. Redesigned our boring B2B SaaS into cyberpunk dystopia theme. Neon colors. Blade Runner vibes. Presented to team Monday thinking it was draft. They LOVED it. Shipped it. Engagement up 300%. TechCrunch called it 'bold design language'. I was trying to make it look like my Spotify."
  },
  {
    author: "BackendBob",
    title: "Refactored database after beer pong tournament",
    text: "Lost beer pong badly. Decided to win at something. Opened laptop. Saw messy database schema. Spent 4 hours normalizing everything. Renamed all tables to Game of Thrones characters. 'users' became 'starks', 'orders' became 'dragons'. Monday morning chaos. Migration scripts everywhere. Took week to fix. Still find references to 'house_lannister_payments' in codebase."
  },
  {
    author: "MarketingMike",
    title: "Tweeted from company account thinking it was personal",
    text: "3am. Arguing with strangers about pineapple on pizza. Forgot logged into @CompanyName with 500k followers. Tweeted: 'Your mom's cooking is mid and you know it'. 50k retweets before I woke up. PR disaster. CEO furious. Somehow engagement skyrocketed. Best performing tweet ever. Now have mandate to 'be more authentic'. Still scared to check Twitter."
  },
  {
    author: "FounderFiona",
    title: "Pivoted entire company strategy in Uber after conference party",
    text: "Web Summit afterparty. Shots with random founders. In Uber ride back, realized our B2B SaaS should be TikTok game. Woke up to 5am email to investors announcing pivot. Raised $2M based on drunk email. Built it. It flopped. Pivoted back. Nobody mentions The Incident. There's a slide about it in our origin story deck labeled 'Vision Evolution'."
  },
  {
    author: "DevOpsDerek",
    title: "Automated my entire job while drunk then forgot about it",
    text: "Frustrated with repetitive deployments during Friday drinks. Wrote bash script to automate everything. Forgot about it. Came Monday. Nothing to do. Script handled everything. Spent 6 months 'working remote' (traveling Asia). Boss loved my 'efficiency'. Eventually script broke. Panicked. Rewrote it sober. Not as good. Miss drunk me's coding skills."
  },
  {
    author: "ProductPete",
    title: "Wrote 47 Jira tickets while drunk, team implemented them all",
    text: "Wine and product roadmap session. Started writing user stories. Got carried away. Created 47 tickets. Morning horror: they're all ridiculous. 'As a user, I want to pet the loading spinner'. Team thought they were brilliant. Implemented ALL of them. App has pettable loading spinner now. Users love it. Became our signature feature. I have no memory of writing it."
  }
];

const DOMAINS = [
  {
    name: "metaverse-for-hamsters.io",
    price: "$12/yr",
    owner: "Available"
  },
  {
    name: "blockchain-toilet.com",
    price: "$4,000",
    owner: "@cryptobro"
  },
  {
    name: "uber-for-hugs.net",
    price: "$9/yr",
    owner: "Available"
  },
  {
    name: "nft-socks-marketplace.xyz",
    price: "$15,000",
    owner: "@sock_millionaire"
  },
  {
    name: "ai-powered-regret.app",
    price: "$25/yr",
    owner: "Available"
  },
  {
    name: "tinder-but-for-vegetables.com",
    price: "$3,500",
    owner: "@vegan_dev"
  },
  {
    name: "proof-of-hangover.eth",
    price: "$890",
    owner: "@drunk_validator"
  },
  {
    name: "saas-for-pets.io",
    price: "$2,100",
    owner: "@pet_founder"
  },
  {
    name: "web3-toaster.com",
    price: "$127/yr",
    owner: "Available"
  },
  {
    name: "420-69-startup-generator.lol",
    price: "$6,969",
    owner: "@meme_entrepreneur"
  }
];

export async function seedHuntItems(userId, userName) {
  console.log('🚀 Seeding hunt items...');
  const results = [];

  for (const item of HUNT_ITEMS) {
    try {
      // Create the hunt item (without comments and features arrays)
      const { comments: itemComments, features: itemFeatures, ...itemData } = item;
      const docRef = await addDoc(collection(db, 'huntItems'), {
        ...itemData,
        authorId: userId,
        author: userName || item.author,
        votes: 0,
        comments: 0,
        upvoters: [],
        downvoters: [],
        createdAt: serverTimestamp()
      });

      const itemId = docRef.id;
      results.push(itemId);

      // Add specific comments for this item
      for (const commentText of itemComments) {
        // Generate random number of likes (0-30) by creating fake liker IDs
        const numLikes = Math.floor(Math.random() * 30);
        const likers = Array.from({ length: numLikes }, (_, i) => `fake_user_${i}`);

        await addDoc(collection(db, 'comments'), {
          itemId,
          itemType: 'huntItem',
          userId: userId,
          userName: userName || 'AnonymousDrunk',
          text: commentText,
          likers: likers,
          createdAt: serverTimestamp()
        });
      }

      // Add specific feature requests for this item
      for (const featureText of itemFeatures) {
        // Generate random number of votes (0-50) by creating fake voter IDs
        const numVotes = Math.floor(Math.random() * 50);
        const voters = Array.from({ length: numVotes }, (_, i) => `fake_voter_${i}`);

        await addDoc(collection(db, 'featureRequests'), {
          itemId,
          userId: userId,
          userName: userName || 'FeatureRequester',
          text: featureText,
          voters: voters,
          createdAt: serverTimestamp()
        });
      }

      console.log(`  ✓ ${item.title} (with ${itemComments.length} comments, ${itemFeatures.length} features)`);
    } catch (error) {
      console.error(`  ✗ Failed to create ${item.title}:`, error);
    }
  }

  return results;
}

export async function seedStories(userId, userName) {
  console.log('📖 Seeding stories...');
  const results = [];

  for (const story of STORIES) {
    try {
      const docRef = await addDoc(collection(db, 'stories'), {
        ...story,
        authorId: userId,
        author: userName || story.author,
        support: Math.floor(Math.random() * 200),
        supporters: [],
        createdAt: serverTimestamp()
      });
      results.push(docRef.id);
      console.log(`  ✓ ${story.title}`);
    } catch (error) {
      console.error(`  ✗ Failed to create story:`, error);
    }
  }

  return results;
}

export async function seedDomains(userId, userName) {
  console.log('💀 Seeding domains...');
  const results = [];

  for (const domain of DOMAINS) {
    try {
      const docRef = await addDoc(collection(db, 'domains'), {
        ...domain,
        // Use placeholder for available domains to satisfy security rules
        ownerId: domain.owner === 'Available' ? userId : userId,
        owner: domain.owner === 'Available' ? 'Available' : (userName || domain.owner),
        isAvailable: domain.owner === 'Available',
        createdAt: serverTimestamp()
      });
      results.push(docRef.id);
      console.log(`  ✓ ${domain.name}`);
    } catch (error) {
      console.error(`  ✗ Failed to create domain:`, error);
    }
  }

  return results;
}

export async function seedAllData(userId, userName) {
  console.log('🌱 Starting seed process...');
  console.log(`User: ${userName} (${userId})`);

  try {
    await seedHuntItems(userId, userName);
    await seedStories(userId, userName);
    await seedDomains(userId, userName);

    console.log('\n✅ Seeding complete!');
    console.log('Refresh the page to see your data.');
  } catch (error) {
    console.error('❌ Seeding failed:', error);
  }
}

// Make it available in window for easy console access
if (typeof window !== 'undefined') {
  window.seedData = {
    seedAllData,
    seedHuntItems,
    seedStories,
    seedDomains
  };
}
